import { Component, ViewEncapsulation, OnInit, OnDestroy, OnChanges, AfterViewInit } from '@angular/core';
import { Router } from "@angular/router";
import { AppState } from '../../app.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalVariables, GlobalRef } from '../../../common/global-variables';
import { DashboardServiceVcd } from './dashboard.service';
import { VcaAlertsService } from '../../../common/vca-alerts/vca-alerts.service';
import { AuthService } from '../../auth/auth.service';
import { AUTH_CONSTANTS } from '../../auth/auth-constants';
import { Subscription } from 'rxjs/Rx';
import { RefreshSharedService } from '../../refresh-shared-service';
import { storagePrettyConverter, checkIfUserHasRoles } from '../../../common/utils/utils';
import { GuidedTour, GuidedTourBuilder } from '../../../app/services/guided-tour-builder/guided-tour-builder.service';
import { DashboardGuidedTourData } from './dashboard.guidedtour.data';

@Component({
    selector: 'dashboard-vcd',
    providers: [DashboardServiceVcd],
    host: {'[class.content-container]': 'true'},
    styles: [],
    encapsulation: ViewEncapsulation.None,
    template: require('./dashboard.html')
})
export default class DashboardVcd implements OnInit, OnDestroy {
    private orgCount: number = 0;
    private vdcCount: number = 0;
    private edgeCount: number = 0;
    private vmCount: number = 0;
    private computeSummary: any;
    private resources: any;
    private computeNetworkCount: number = 0;
    private directConnectCount: number = 0;
    private internetNetworkCount: number = 0;
    private transitNetworkCount: number = 0;
    private publicFreeIps: number = 0;
    private publicTotalIps: number = 0;
    private directConnectFreeIps: number = 0;
    private directConnectTotalIps: number = 0;
    private internetNetworkIds: Array<string> = [];
    private directConnectIds: Array<string> = [];
    private vcCount: number = 0;
    private authConstants = AUTH_CONSTANTS;
    private isTranslationready: boolean = true;
    private showOrgCount = true;
    private rsService: RefreshSharedService;
    private refreshSubscription: Subscription;
    private currentClassName: string = '';
    private loaded: boolean = false;
    private admininstratorView: boolean = false;

    private subComponentsLoaded:  number = 0;
    private guidedTourData: DashboardGuidedTourData;
    private allNetworks;

    constructor(public appState: AppState,
                public translate: TranslateService,
                public authService: AuthService,
                public dashboardService: DashboardServiceVcd,
                public alerts: VcaAlertsService,
                private router: Router,
                private globalRef: GlobalRef,
                private guidedTourBuilder: GuidedTourBuilder,
                private rss: RefreshSharedService) {
        translate.use(GlobalVariables.LOCALE);
        this.resources = [];
        let roles = (this.authService.loggedInUserInfo && this.authService.loggedInUserInfo.roles) ? this.authService.loggedInUserInfo.roles : [];
        if ( checkIfUserHasRoles(roles) ) {
            this.admininstratorView = true;
        } else {
            this.admininstratorView = false;
        }
        this.rsService = rss;
        this.currentClassName = 'DashboardVcd';


        this.guidedTourData = new DashboardGuidedTourData(this.admininstratorView,
            this.translate, this.authService, this.dashboardService,
            this.globalRef, this.guidedTourBuilder);
    }

    ngOnInit() {
        this.refreshSubscription = this.rsService.getRefreshParam()
        .subscribe (
            item => {
            this.handleEmittedValues(item);
        });
         this.rsService.setParam({
            'loadedComponent': this.currentClassName,
            'detailPageIDName': '',
            'shouldReloadInventoryTree': false
        });
        this.getLandingPageData();
    }


    handleEmittedValues(item) {
        if (item.loadedComponent === this.currentClassName) {
            this.resources = [];
            this.getLandingPageData();
        }
    }

    checkAndStartGuidedTour(lambda: ()=>boolean) {
        if(lambda()) {
            this.guidedTourData.startOverviewTourIfNotBlocked();
        }
    }

    getLandingPageData() {
        this.loaded = false;
        if (this.authService.VREALM_TYPE === this.authConstants.VCD) {
            this.dashboardService.getComputeSummary().subscribe(
                data => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 1;
                    } );
                    this.computeSummary = data;
                    this.orgCount = this.computeSummary.orgCount;
                    this.vdcCount = this.computeSummary.vdcCount;
                    this.vmCount = this.computeSummary.vm
                    && this.computeSummary.vm.vmCount
                        ? this.computeSummary.vm.vmCount
                        : 0;

                    let properties = this.computeSummary.properties;
                    if (properties) {
                        let cpuStats = properties.cpu;
                        if (cpuStats) {
                            this.resources.push(this.getResourceFromStats(cpuStats, 'cpu'));
                        }
                        let memoryStats = properties.memory;
                        if (memoryStats) {
                            this.resources.push(this.getResourceFromStats(memoryStats, 'memory'));
                        }
                        let storageStats = properties.storage;
                        if (storageStats) {
                            this.resources.push(this.getResourceFromStats(storageStats, 'storage'));
                        }
                    }

                },
                error => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 1;
                    } );

                    this.alerts.danger(['Failed to retrieve Compute Summary', error], 'get-calls-error-alert');
                }
            );
        } else {
            this.dashboardService.getVcs().subscribe(
                (data: any) => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 2;
                    } );
                    let vcs: Array<any> = data;
                    if (vcs) {
                        this.vcCount = vcs.length;
                    }
                },
                error => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 2;
                    } );
                    this.alerts.danger(
                        [this.translate.instant('dashboard.vcRetrieveError'), error],
                        'get-calls-error-alert');
                }
            );

            this.dashboardService.getVMsForVC().subscribe(
                (data: any) => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 2;
                    } );
                    let vms: Array<any> = data;
                    if (vms) {
                        this.vmCount = vms.length;
                    }
                },
                error => {
                    this.loaded = true;
                    this.checkAndStartGuidedTour( () => {
                        return ++this.subComponentsLoaded >= 2;
                    } );
                    this.alerts.danger(
                        [this.translate.instant('dashboard.vmRetrieveError'), error],
                        'get-calls-error-alert');
                }
            );
        }

        this.dashboardService.getAllGateways()
            .subscribe(
                (data: any) => {
                    let gateways: Array<any> = data;
                    if (gateways) {
                        this.edgeCount = gateways.length;
                    }
                },
                error => {
                    this.alerts.danger(
                        [this.translate.instant('networkDashboard.gatewayRetrieveError'), error],
                        'get-calls-error-alert');
                }
            );

        if (this.authService.VREALM_TYPE === this.authConstants.VCD) {

            this.dashboardService.getDirectConnectList()
                .subscribe(
                    (data: any) => {
                        if (data) {
                            this.directConnectCount = data.length;
                        }
                    },
                    error => {
                        this.alerts.danger(
                            [this.translate.instant('networkDashboard.directConnectRetrieveError'), error],
                            'get-calls-error-alert');
                    }
                );

            this.dashboardService.getVDCNetworkList()
                .subscribe(
                    (data: any) => {
                        if (data) {
                            this.computeNetworkCount = data.length;
                        }
                    },
                    error => {
                        this.alerts.danger(
                            [this.translate.instant('networkDashboard.vdcNetworkRetrieveError'), error],
                            'get-calls-error-alert');
                    }
                );

        } else {
            this.dashboardService.getAllNetworks()
                .subscribe(
                    (data: any) => {
                        this.computeNetworkCount = 0;
                        this.transitNetworkCount = 0;
                        this.internetNetworkCount = 0;
                        this.directConnectCount = 0;
                        this.publicFreeIps = 0;
                        this.publicTotalIps = 0;
                        this.directConnectFreeIps = 0;
                        this.directConnectTotalIps = 0;
                        this.directConnectIds = [];
                        this.internetNetworkIds = [];
                        let networkList = data;
                        this.allNetworks = data;
                        if (networkList) {
                            for (let i = 0; i < networkList.length; i++) {
                                let network = networkList[i];
                                let isL3Managed = network.l3TenantManaged;
                                let isOwnedBySystem = network.ownedBySystem;
                                let networkScope: Array<string> = network.scope;

                                if (isL3Managed && isOwnedBySystem) {
                                    if (network.objectId) {
                                        this.directConnectIds.push(network.objectId);
                                    }
                                    this.directConnectCount++;
                                } else if (isL3Managed && !isOwnedBySystem) {
                                    if (networkScope && networkScope.length > 0) {
                                        this.computeNetworkCount++;
                                    } else {
                                        this.transitNetworkCount++;
                                    }
                                } else if (!isL3Managed && isOwnedBySystem) {
                                        if (network.objectId) {
                                            this.internetNetworkIds.push(network.objectId);
                                        }
                                        this.internetNetworkCount++;
                                    }
                                }
                            }

                        let networkIdsLength = this.internetNetworkIds.length;
                        let responseCount = networkIdsLength;
                        for (let i = 0; i < networkIdsLength; i++) {
                            let networkId = this.internetNetworkIds[i];
                            this.dashboardService.getIpUsage(networkId).subscribe(
                                (ipData: any) => {
                                    responseCount--;
                                    let ipUsage = ipData;
                                    if (ipUsage) {
                                        if (ipUsage.freeIps !== null
                                            && typeof ipUsage.freeIps !== 'undefined'
                                            && ipUsage.totalIps !== null
                                            && typeof ipUsage.totalIps !== 'undefined') {
                                            this.publicFreeIps = ipUsage.freeIps;
                                            this.publicTotalIps = ipUsage.totalIps;
                                        }
                                    }

                                    let ipStats: any = {
                                        used: this.publicTotalIps - this.publicFreeIps,
                                        allocated: this.publicTotalIps,
                                        unit: ''
                                    };

                                    if (this.publicTotalIps !== 0) {
                                        let networkName = this.getNetworkName(networkId);
                                        this.resources.push(this.getResourceFromStats(ipStats, 'ips', networkName));
                                    }

                                },
                                error => {
                                    this.alerts.danger(
                                        [this.translate.instant('networkDashboard.ipUsageRetrieveError') + networkId,
                                            error], 'get-calls-error-alert');
                                }
                            );
                        }

                        let directConnectNetworkIdsLength = this.directConnectIds.length;
                        let directConnectResponseCount = directConnectNetworkIdsLength;
                        for (let i = 0; i < directConnectNetworkIdsLength; i++) {
                            let networkId = this.directConnectIds[i];
                            this.dashboardService.getIpUsage(networkId).subscribe(
                                (ipData: any) => {
                                    directConnectResponseCount--;
                                    let directConnectIpUsage = ipData;
                                    if (directConnectIpUsage) {
                                        if (directConnectIpUsage.freeIps !== null
                                            && typeof directConnectIpUsage !== 'undefined'
                                            && directConnectIpUsage.totalIps !== null
                                            && typeof directConnectIpUsage.totalIps !== 'undefined') {
                                            this.directConnectFreeIps = directConnectIpUsage.freeIps;
                                            this.directConnectTotalIps = directConnectIpUsage.totalIps;
                                        }
                                    }
                                        let ipStats: any = {
                                            used: this.directConnectTotalIps - this.directConnectFreeIps,
                                            allocated: this.directConnectTotalIps,
                                            unit: ''
                                        };
                                        if (this.directConnectTotalIps !== 0) {
                                            let networkName = this.getNetworkName(networkId);
                                            this.resources.push(this.getResourceFromStats(ipStats, 'directIps', networkName));
                                        }
                                },
                                error => {
                                    this.alerts.danger(
                                        [this.translate.instant('networkDashboard.ipUsageRetrieveError') + networkId,
                                            error], 'get-calls-error-alert');
                                }
                            );
                        }
                    },
                    error => {
                        this.alerts.danger(
                            [this.translate.instant('networkDashboard.networkRetrieveError'), error],
                            'get-calls-error-alert');
                    }
                );
        }

    }

    getNetworkName(networkId) {
        let network = this.allNetworks.find(o => o.objectId === networkId);
        if (network && network.name) {
            return network.name;
        }
            return '';
    }

    getResourceFromStats(stats: any, resourceName: string, networkName: string = '') {
        let used = stats.used;
        let total = stats.allocated;
        let unit = stats.unit;
        let percentage;

        if (total && total !== 0) {
            percentage = ((used / total) * 100).toFixed(0);
        }

        let labelText = this.translate.instant('computeModule.dashboard.dashboard.' + resourceName);
        let result =  {
            label: labelText,
            used: used,
            total: total,
            percentage: percentage,
            unit: unit,
            usedWithUnit: storagePrettyConverter( used, unit ),
            totalWithUnit: storagePrettyConverter( total, unit ),
            warning: this.translate.instant('dashboard.warningPre') + ' ' + labelText,
            resourceName: resourceName,
            shortName: ''
        };

        if (resourceName === 'ips' || resourceName === 'directIps') {
            result.warning = this.translate.instant('dashboard.ipsConsumed', {percentageUsed: percentage, networkName: networkName});
            result.label = networkName + ' IPs';
        }
        let strLabel: string = result.label
        result.shortName = strLabel.length > 25 ? strLabel.substring(0, 22) + '...' : strLabel;
        return result;
    }

    isResourceRunningOut(usagePercent: number, label: string) {
        if (label === 'ips' || label === 'directIps') {
            return false;
        }
        return usagePercent > 90;
    }

    viewActivityLog() {
        this.router.navigate(['/activities']);
    }

   ngOnDestroy() {
        this.rsService.resetParam();
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }

        this.guidedTourData.parkToursAndCleanup();
   }

}



// WEBPACK FOOTER //
// ./src/app/components/dashboard/dashboard.component.ts